import { useApp } from '../context';
import { displayTitle, isWatched, progress, subtitle, tileFlags, tmdbRef, type PlexItem } from '../plex';
import { TileFlags, TileScore } from './MediaFlags';
import { setFocusBackdrop, useExternalArt } from '../meta';
import { linkProps } from '../router';
import { useCardMenu } from './CardMenu';

export type CardKind = 'poster' | 'landscape' | 'wide';

/** Poster- of landscape-tegel. Klikken opent de detailpagina. */
export function Card({ item, kind = 'poster', className = '' }: { item: PlexItem; kind?: CardKind; className?: string }) {
  const { plex } = useApp();
  const landscape = kind !== 'poster';
  const isEp = item.type === 'episode';
  const ext = useExternalArt(tmdbRef(item));
  const plexImg = landscape
    ? plex.image(isEp ? item.thumb ?? item.grandparentArt : item.art ?? item.thumb, 480, 270)
    : plex.image(isEp ? item.grandparentThumb ?? item.parentThumb : item.thumb, 260, 390);
  // Gekozen posterbron (TMDB/Fanart.tv) als die er is, anders Plex (met eventuele Overlayrr-posters)
  const img = (landscape ? ext?.backdrop : ext?.poster) ?? plexImg;
  const onFocus = () => {
    if (document.documentElement.dataset.backdrop !== 'focus') return;
    setFocusBackdrop(ext?.backdrop ?? plex.image(item.art ?? item.grandparentArt, 1920, 1080));
  };
  const p = progress(item);
  const title = displayTitle(item);
  const sub = isEp ? `S${item.parentIndex ?? '?'} E${item.index ?? '?'} · ${item.title}` : subtitle(item);
  const menu = useCardMenu({
    ratingKey: item.ratingKey,
    type: item.type,
    viewOffset: item.viewOffset,
    duration: item.duration,
    showKey: isEp ? item.grandparentRatingKey : undefined,
    guid: isEp ? item.grandparentGuid : item.guid,
    watched: isWatched(item),
  }, title);

  return (
    <a
      className={`card ${landscape ? 'card-landscape' : 'card-poster'} ${className}`}
      {...linkProps(`/item/${item.ratingKey}`)}
      aria-label={`${title}${sub ? `, ${sub}` : ''}`}
      onFocus={onFocus}
      onMouseEnter={onFocus}
      {...menu.props}
    >
      <div className="card-art" style={img ? { backgroundImage: `url("${img}")` } : undefined}>
        {!img && <span className="card-fallback">{title}</span>}
        <TileFlags flags={tileFlags(item)} />
        <TileScore score={item.audienceRating ?? item.rating} />
        {isWatched(item) && p === undefined && (
          <span className="watched-mark" aria-label="Gezien">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true"><path d="M5 12l5 5 9-11" /></svg>
          </span>
        )}
        {p !== undefined && (
          <div className="progress"><div style={{ width: `${Math.round(p * 100)}%` }} /></div>
        )}
        {menu.dots}
      </div>
      <span className="card-title">{title}</span>
      {sub && <span className="card-sub">{sub}</span>}
    </a>
  );
}

export function Row({ title, items, kind }: { title?: string; items: PlexItem[]; kind?: CardKind }) {
  return (
    <section className="row" aria-label={title}>
      {title && <h2>{title}</h2>}
      <div className="row-track" data-focus-group>
        {items.map((item) => <Card key={item.ratingKey} item={item} kind={kind} />)}
      </div>
    </section>
  );
}
